import { Capacitor } from '@capacitor/core'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AppProvider, useApp } from './hooks/useApp'
import { emailConfirmation, signInDestination } from './lib/signInFlow'
import Navigation from './components/Navigation'
import Toast from './components/Toast'
import NativeAppearance from './components/NativeAppearance'
import Home from './pages/Home'
import Sessions from './pages/Sessions'
import SessionDetail from './pages/SessionDetail'
import SessionPlayer from './pages/SessionPlayer'
import CustomAudio from './pages/CustomAudio'
import Premium from './pages/Premium'
import Success from './pages/Success'
import Onboarding from './pages/Onboarding'
import Program from './pages/Program'
import Courses, { Course } from './pages/Courses'
import Care from './pages/Care'
import CareNotificationRouter from './components/CareNotificationRouter'
import DapPurchase from './pages/DapPurchase'
import SignIn from './pages/SignIn'
import AccountDeletion from './pages/AccountDeletion'
import QuickReset from './pages/QuickReset'
import AboutMatthew from './pages/AboutMatthew'
import LegalDocument from './pages/LegalDocument'
import privacyPolicy from './content/privacy-policy.js'
import { aboutMatthewProfile } from './config/aboutMatthew'

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <CareNotificationRouter />
        <NativeAppearance />
        <AppShell />
      </BrowserRouter>
    </AppProvider>
  )
}

function AppShell() {
  // Build-time check first, so the native bundle drops the web purchase pages and Stripe.
  const nativeApp = import.meta.env.MODE === 'native' || Capacitor.isNativePlatform()
  // iOS v1 ships without Support and My courses (#101 step 4.3). Android ships the same bundle, so this is a run-time check.
  const ios = Capacitor.getPlatform() === 'ios'
  // Keyed on the path so every screen change fades in (index.css .screen).
  const { pathname, hash } = useLocation()
  const { authReady, authUser, authError } = useApp()
  if (pathname !== '/signin' && emailConfirmation(hash)) {
    return <Navigate to={`/signin?next=${encodeURIComponent(signInDestination(pathname))}${hash}`} replace />
  }
  if (authReady && !authUser && authError && pathname !== '/signin') {
    return <Navigate to={`/signin?next=${encodeURIComponent(signInDestination(pathname))}`} replace />
  }
  const showNavigation = ['/', '/program', '/courses', '/care', '/sessions', '/premium', '/about'].includes(pathname)
    || pathname.startsWith('/courses/')
  const isPlayer = pathname === '/reset' || pathname.startsWith('/sessions/') && pathname.endsWith('/play')
  return (
    <div className={`app-shell${showNavigation ? ' app-shell-with-nav' : ''}${isPlayer ? ' app-shell-player' : ''}${Capacitor.isNativePlatform() ? ' app-shell-native' : ''}`}>
      <Toast />
      <div className="screen" key={pathname}>
      <Routes>
        <Route path="/welcome" element={<Onboarding />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/delete-account" element={<AccountDeletion />} />
        <Route path="/privacy" element={<LegalDocument html={privacyPolicy} />} />
        <Route path="/" element={<Home />} />
        <Route path="/reset" element={<QuickReset />} />
        <Route path="/program" element={<Program />} />
        <Route path="/courses" element={ios ? <Navigate to="/" replace /> : <Courses />} />
        <Route path="/courses/:courseId" element={ios ? <Navigate to="/" replace /> : <Course />} />
        <Route path="/dap" element={nativeApp ? <Navigate to="/premium" replace /> : <DapPurchase />} />
        <Route path="/care" element={ios ? <Navigate to="/" replace /> : <Care />} />
        <Route path="/sessions" element={<Sessions />} />
        <Route path="/sessions/:id" element={<SessionDetail />} />
        <Route path="/sessions/:id/play" element={<SessionPlayer />} />
        <Route path="/custom" element={nativeApp ? <Navigate to="/premium" replace /> : <CustomAudio />} />
        <Route path="/premium" element={<Premium />} />
        <Route path="/about" element={<AboutMatthew profile={aboutMatthewProfile} />} />
        <Route path="/success" element={nativeApp ? <Navigate to="/premium" replace /> : <Success />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </div>
      {showNavigation && <Navigation />}
    </div>
  )
}
